import { useState } from "react";
import { CheckCircle } from "@mui/icons-material";
import styles from "../../styles/results.module.css";
import commonStyles from "../../styles/common.module.css";
import { useTranslation } from "react-i18next";
import { SUPPORT_EMAIL } from "../../config";
import { getApplicationStatusApi } from "../../api/application";
import { ApplicationStatus } from "../../types/home";
import { useClientSession } from "../../provider/ClientSessionProvider";

/**
 * Submission confirmation — the terminal screen (phase 5).
 *
 * The flow is fire and forget. There is no account to come back to and no
 * polling: the application reference shown here is the only handle on the
 * submission, and status is fetched only if the applicant asks for it while
 * still on this screen.
 *
 * Leaving ends the session and discards the reference, which is why it is
 * presented as something to keep rather than something to glance at.
 */

interface Props {
  applicationId: string;
}

const SubmissionConfirmation = ({ applicationId }: Props) => {
  const { t } = useTranslation();
  const { endSession } = useClientSession();

  const [status, setStatus] = useState<ApplicationStatus | null>(null);
  const [isChecking, setIsChecking] = useState(false);
  const [checkFailed, setCheckFailed] = useState(false);

  async function handleCheckStatus() {
    if (isChecking) return;
    setIsChecking(true);
    setCheckFailed(false);
    try {
      const response = await getApplicationStatusApi(applicationId);
      const next = response?.data?.applicationStatus as ApplicationStatus | undefined;
      // A submitted application with no status yet is still under review.
      setStatus(next ?? ApplicationStatus.PENDING);
    } catch {
      setCheckFailed(true);
    } finally {
      setIsChecking(false);
    }
  }

  function statusLabel(value: ApplicationStatus): string {
    switch (value) {
      case ApplicationStatus.APPROVED:
        return t("status.approved");
      case ApplicationStatus.REJECTED:
        return t("status.rejected");
      default:
        return t("status.inreview");
    }
  }

  return (
    <div className={styles.successSection}>
      <div className={commonStyles.successBadgeSummary}>
        <CheckCircle sx={{ color: "var(--color-success)" }} />
        <div className={commonStyles.successHeaderSummary}>
          {t("submit.successbadge")}
        </div>
      </div>

      {/* The reference is the only way back, so it leads rather than trails. */}
      <div className={styles.referenceBlock}>
        <span className={styles.referenceLabel}>{t("submit.reference.label")}</span>
        <span className={styles.referenceValue}>{applicationId}</span>
        <span className={styles.referenceHint}>{t("submit.reference.hint")}</span>
      </div>

      <div className={commonStyles.successInstructionContainer}>
        <div className={commonStyles.successInstruction}>
          <span className={commonStyles.successInstructionHeader}>
            {t("submit.instruction.header2")}
          </span>
          <span className={commonStyles.successInstructionDescription}>
            {t("submit.instruction.description2")}
          </span>
        </div>
        <div className={commonStyles.successInstruction}>
          <span className={commonStyles.successInstructionHeader}>
            {t("submit.instruction.header3")}
          </span>
          <span className={commonStyles.successInstructionDescription}>
            {t("email")}{" "}
            <a href={`mailto:${SUPPORT_EMAIL}`} className={commonStyles.link}>
              {SUPPORT_EMAIL}{" "}
            </a>
            {t("submit.instruction.description3")}
          </span>
        </div>
      </div>

      <div className={styles.statusBlock}>
        {status !== null && (
          <p role="status" className={styles.statusResult}>
            {statusLabel(status)}
          </p>
        )}
        {checkFailed && (
          <p role="alert" className={styles.statusError}>
            {t("status.checkfailed")}
          </p>
        )}
        <button
          type="button"
          className={isChecking ? styles.button_secondary_disabled : styles.button_secondary}
          onClick={handleCheckStatus}
          disabled={isChecking}
        >
          {isChecking ? t("status.checking") : t("button.checkstatus")}
        </button>
      </div>

      <button type="button" className={styles.button_primary} onClick={endSession}>
        {t("button.finish")}
      </button>
    </div>
  );
};

export default SubmissionConfirmation;
